import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';
import { breakpoints } from '../design-system/tokens/breakpoints';

type LanguageCode = 'en' | 'gr';

interface LanguageOption {
  code: LanguageCode;
  /** Compact label shown in the control */
  shortLabel: string;
  /** Accessible name in the language itself (endonym) */
  nativeName: string;
  htmlLang: string;
}

/** Always use endonyms so users can find their language in any UI language. */
const LANGUAGES: LanguageOption[] = [
  { code: 'en', shortLabel: 'EN', nativeName: 'English', htmlLang: 'en' },
  { code: 'gr', shortLabel: 'ΕΛ', nativeName: 'Ελληνικά', htmlLang: 'el' },
];

const Switcher = styled.div`
  display: inline-flex;
  align-items: stretch;
  flex-shrink: 0;
  padding: 3px;
  background: ${props => props.theme.colors.surface};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  min-height: 44px;
`;

const Option = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 38px;
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  border-radius: ${borders.radius.md};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${props =>
    props.$active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  letter-spacing: 0.02em;
  line-height: 1;
  color: ${props =>
    props.$active ? props.theme.colors.surface : props.theme.colors.textSecondary};
  background: ${props =>
    props.$active ? props.theme.colors.primary : 'transparent'};
  transition: ${animations.transition(
    ['background-color', 'color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: ${breakpoints.lg}) {
    min-width: 44px;
    padding: ${spacing[2]} ${spacing[2]};
  }

  &:hover {
    color: ${props =>
      props.$active ? props.theme.colors.surface : props.theme.colors.text};
    background: ${props =>
      props.$active ? props.theme.colors.primary : props.theme.colors.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    z-index: 1;
  }
`;

function normalizeLanguage(code: string): LanguageCode {
  if (code === 'gr' || code.startsWith('el')) return 'gr';
  return 'en';
}

/**
 * Compact bilingual language switcher (segmented control).
 * Prefer this over a dropdown when there are only two languages:
 * one click, both options visible, clearer selected state.
 */
const LanguageDropdown: React.FC = () => {
  const { i18n, t } = useTranslation();
  const groupId = useId();
  const optionRefs = useRef<Partial<Record<LanguageCode, HTMLButtonElement | null>>>({});
  const current = normalizeLanguage(i18n.language);

  useEffect(() => {
    const active = LANGUAGES.find(l => l.code === current) ?? LANGUAGES[0];
    document.documentElement.lang = active.htmlLang;
  }, [current]);

  const handleSelect = (code: LanguageCode, moveFocus = false) => {
    if (code !== current) {
      void i18n.changeLanguage(code);
    }
    if (moveFocus) {
      // Focus after React commits the new selected state
      requestAnimationFrame(() => {
        optionRefs.current[code]?.focus();
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = LANGUAGES.findIndex(l => l.code === current);
    const next =
      event.key === 'ArrowRight'
        ? LANGUAGES[(index + 1) % LANGUAGES.length]
        : LANGUAGES[(index - 1 + LANGUAGES.length) % LANGUAGES.length];
    handleSelect(next.code, true);
  };

  return (
    <Switcher
      role="radiogroup"
      id={groupId}
      aria-label={t('language.select')}
      onKeyDown={handleKeyDown}
    >
      {LANGUAGES.map(language => {
        const active = language.code === current;
        return (
          <Option
            key={language.code}
            ref={el => {
              optionRefs.current[language.code] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={language.nativeName}
            title={language.nativeName}
            $active={active}
            tabIndex={active ? 0 : -1}
            onClick={() => handleSelect(language.code)}
          >
            {language.shortLabel}
          </Option>
        );
      })}
    </Switcher>
  );
};

export default LanguageDropdown;
